import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Job, User } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import AssignmentRateSection from '@/modules/jobs/components/create/AssignmentRateSection';
import {
  EMPTY_ASSIGNMENT_RATE_INPUT,
  getAssignmentRateOverrides,
  type AssignmentRateInput,
} from '@/modules/jobs/components/create/assignmentRateInputs';
import useCustomAssignmentRatesAccess from '@/modules/jobs/hooks/useCustomAssignmentRatesAccess';

interface WorkerSearchbarActionsProps {
  job: Job;
  worker: User | null;
  setLoading: (loading: boolean) => void;
  onCancel: () => void;
  onAssigned?: () => void | Promise<Job | null>;
}

const WorkerSearchbarActions = ({
  job,
  worker,
  setLoading,
  onCancel,
  onAssigned,
}: WorkerSearchbarActionsProps) => {
  const { assignWorkerToJob } = useJobStore();
  const { getTerm } = useTemplateStore();
  const canSetCustomAssignmentRates = useCustomAssignmentRatesAccess(
    job.tenantId,
  );
  const [assignmentRateInput, setAssignmentRateInput] =
    useState<AssignmentRateInput>(EMPTY_ASSIGNMENT_RATE_INPUT);
  const [isEditingAssignmentRates, setIsEditingAssignmentRates] =
    useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const assignInFlightRef = useRef(false);
  const fetchClassificationLevelClientRates = useClassificationStore(
    (state) => state.fetchClassificationLevelClientRates,
  );

  useEffect(() => {
    if (canSetCustomAssignmentRates && job.clientId) {
      void fetchClassificationLevelClientRates(job.clientId);
    }
  }, [
    canSetCustomAssignmentRates,
    fetchClassificationLevelClientRates,
    job.clientId,
  ]);

  useEffect(() => {
    setAssignmentRateInput(EMPTY_ASSIGNMENT_RATE_INPUT);
    setIsEditingAssignmentRates(false);
  }, [worker?.id]);

  const handleAssignWorker = async (worker: User) => {
    try {
      await assignWorkerToJob(
        job.id,
        worker.id,
        getAssignmentRateOverrides(assignmentRateInput),
      );
      toast.success(
        `${worker.firstName} ${worker.lastName} successfully assigned to job!`,
      );
      await onAssigned?.();
    } catch (error) {
      console.error('Error assigning worker to job:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Error assigning worker to job',
      );
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {canSetCustomAssignmentRates && worker && !isEditingAssignmentRates && (
        <button
          type="button"
          onClick={() => setIsEditingAssignmentRates(true)}
          className="self-end text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Set a custom rate
        </button>
      )}
      {canSetCustomAssignmentRates && worker && isEditingAssignmentRates && (
        <div>
          <AssignmentRateSection
            worker={worker}
            clientId={job.clientId}
            classificationId={job.classificationId}
            rateInput={assignmentRateInput}
            ordinaryWorkerPayRateCents={
              job.potentialPayRates?.potentialWorkerPayRateForUserIdInCents
            }
            ordinaryClientPayRateCents={
              job.potentialPayRates?.potentialClientPayRateCeilingInCents
            }
            onChange={(updates) =>
              setAssignmentRateInput((currentRateInput) => ({
                ...currentRateInput,
                ...updates,
              }))
            }
          />
          <button
            type="button"
            onClick={() => {
              setAssignmentRateInput(EMPTY_ASSIGNMENT_RATE_INPUT);
              setIsEditingAssignmentRates(false);
            }}
            className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Use ordinary rates
          </button>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-gray-100 hover:text-blue-500"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!worker || isAssigning}
          title={
            !worker
              ? `No ${getTerm(TemplateTerm.WORKER, false)} selected`
              : `Assign ${getTerm(TemplateTerm.WORKER, false)}`
          }
          onClick={() => {
            if (!worker || assignInFlightRef.current) {
              return;
            }
            assignInFlightRef.current = true;
            setIsAssigning(true);
            setLoading(true);
            void handleAssignWorker(worker).finally(() => {
              assignInFlightRef.current = false;
              setIsAssigning(false);
              setLoading(false);
            });
          }}
          className={`rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 ${
            !worker || isAssigning ? 'cursor-not-allowed opacity-50' : ''
          }`}
        >
          {isAssigning ? 'Assigning...' : 'Assign'}
        </button>
      </div>
    </div>
  );
};

export default WorkerSearchbarActions;
